"use client";

import React, { startTransition, useEffect, useRef, useState } from "react";
import { withSafeRender } from "../../atoms/safe-render";

/* One section per idle slice, so prebuilding Add-on and MyFree never lands
   as one long task. Module-level because the sections are siblings with no
   common client parent to own the queue. */
const queue: Array<() => void> = [];
let cancelPending: (() => void) | null = null;

function idle(callback: () => void): () => void {
  if (typeof window !== "undefined" && window.requestIdleCallback) {
    const id = window.requestIdleCallback(callback);
    return () => window.cancelIdleCallback(id);
  }
  const id = setTimeout(callback, 1);
  return () => clearTimeout(id);
}

function drain(): void {
  cancelPending = null;
  queue.shift()?.();
  if (queue.length > 0) {
    cancelPending = idle(drain);
  }
}

function enqueue(build: () => void): () => void {
  queue.push(build);
  cancelPending ??= idle(drain);
  return () => {
    const index = queue.indexOf(build);
    if (index !== -1) {
      queue.splice(index, 1);
    }
    /* Nothing left to build: drop the pending slice so it cannot go stale. */
    if (queue.length === 0 && cancelPending) {
      cancelPending();
      cancelPending = null;
    }
  };
}

/* Busy pages (third-party tags) may never go idle, so a section nearing the
   viewport also builds. One observer for every section; it watches the
   <details>, since a closed section's body has no box to intersect. */
const NEAR_VIEWPORT_MARGIN = "800px 0px";
type Build = () => void;
const nearBuilds: Map<Element, Build> = new Map();
let nearObserver: IntersectionObserver | null = null;

function observeNear(target: Element, build: () => void): () => void {
  if (typeof IntersectionObserver === "undefined") {
    return () => {};
  }
  nearObserver ??= new IntersectionObserver(
    entries => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          nearBuilds.get(entry.target)?.();
        }
      }
    },
    { rootMargin: NEAR_VIEWPORT_MARGIN }
  );
  nearBuilds.set(target, build);
  nearObserver.observe(target);
  return () => {
    nearBuilds.delete(target);
    nearObserver?.unobserve(target);
  };
}

export interface LazySectionBodyProps {
  /** GS7 — the section's server `open` state. Open sections render their rows
   *  on the server; closed ones render none until opened or prebuilt. */
  defaultOpen: boolean;
  /** Layout only. */
  className?: string;
  /** Always drawn, ahead of the rows (GS11's MyFree lockup). */
  introSlot?: React.ReactNode;
  children: React.ReactNode;
}

/** The body of a grid section. */
function LazySectionBodyImpl({ defaultOpen, className, introSlot, children }: LazySectionBodyProps) {
  const [built, setBuilt] = useState(defaultOpen);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (built) {
      return undefined;
    }
    const details = ref.current?.closest("details");
    /* Opened before the idle slice came round: build now, in the urgent lane,
       because the reader is looking at an empty body. */
    const onToggle = () => {
      if (details?.open) {
        setBuilt(true);
      }
    };
    details?.addEventListener("toggle", onToggle);
    const build = () => startTransition(() => setBuilt(true));
    const cancel = enqueue(build);
    const target = details ?? ref.current;
    const unobserve = target ? observeNear(target, build) : () => {};

    return () => {
      details?.removeEventListener("toggle", onToggle);
      cancel();
      unobserve();
    };
  }, [built]);

  return (
    <div className={className} ref={ref}>
      {introSlot}
      {built ? children : null}
    </div>
  );
}
export const LazySectionBody = withSafeRender(LazySectionBodyImpl, "LazySectionBody");

export default LazySectionBody;
